import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Input, Text } from '@Sage/hoi-poi-ui';
import ModalHoi from 'components/ModalHoi';
import { getLiteral } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import { createDomain } from 'services/DomainService';

const INITIAL_FORM_STATE = {
    domainName: '',
};

const CreateDomainModal = ({ isOpen, onCancel, onRequestClose, onSuccess }) => {
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) return;
        setFormData(INITIAL_FORM_STATE);
        setIsSubmitting(false);
    }, [isOpen]);

    const validateDomain = useCallback((domain) => {
        if (!domain || domain.trim().length === 0) {
            return getLiteral('validation_required_field');
        }
        if (domain.trim().length < 3) {
            return getLiteral('validation_min_length');
        }
        if (!domain.includes('.')) {
            return getLiteral('validation_invalid_domain_format');
        }
        return null;
    }, []);

    const handleDomainChange = useCallback((value) => {
        setFormData((prev) => ({ ...prev, domainName: value }));
    }, []);

    const handleClose = useCallback(() => {
        setFormData(INITIAL_FORM_STATE);
        setIsSubmitting(false);
        onCancel?.();
    }, [onCancel]);

    const isFormValid = useMemo(() => {
        const validationError = validateDomain(formData.domainName);
        return !validationError && !isSubmitting;
    }, [formData.domainName, validateDomain, isSubmitting]);

    const handleSubmit = useCallback(() => {
        const validationError = validateDomain(formData.domainName);
        if (validationError) {
            return;
        }

        setIsSubmitting(true);

        createDomain({
            domain: formData.domainName.trim(),
        })
            .then((responseData) => {
                successToast({
                    text: getLiteral('succes_entitycreatedsuccessfully'),
                });
                onSuccess?.(responseData);
                handleClose();
            })
            .catch(() => {
                errorToast({
                    text: getLiteral('label_failed_create'),
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [formData.domainName, validateDomain, onSuccess, handleClose]);

    return (
        <ModalHoi
            className="marketing-create-domain-modal"
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            onCancel={handleClose}
            onConfirm={handleSubmit}
            title={getLiteral('title_marketing_campaigns_domain_information_modal')}
            cancelText={getLiteral('action_cancel')}
            confirmText={getLiteral('action_marketing_campaigns_obtain_dns_records')}
            isConfirmLoading={isSubmitting}
            useHeader
            useCornerClose
            isConfirmDisabled={!isFormValid}
        >
            <div className="marketing-create-domain-modal__content">
                <Text className="marketing-create-domain-modal__description">
                    {getLiteral('label_marketing_campaigns_domain_modal_enter_domain_name_desc')}
                </Text>
                <Input
                    label={getLiteral('label_marketing_campaigns_domain')}
                    placeholder={getLiteral('label_marketing_campaigns_domain_placeholder')}
                    value={formData.domainName}
                    onChange={handleDomainChange}
                    isRequired
                    isFullWidth
                    autoFocus
                />
            </div>
        </ModalHoi>
    );
};

CreateDomainModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onCancel: PropTypes.func,
    onRequestClose: PropTypes.func,
    onSuccess: PropTypes.func,
};

CreateDomainModal.displayName = 'CreateDomainModal';

export default memo(CreateDomainModal);
